import type { MessageKey, Translate } from "../i18n";

// Stage names for the climb (ADR-0043): one per engine step on the default five-step track
// (ADR-0040). The engine decides the step; the app only names it.

const STAGES = [
  "progress.stage.0",
  "progress.stage.1",
  "progress.stage.2",
  "progress.stage.3",
  "progress.stage.4",
  "progress.stage.5",
] as const satisfies readonly MessageKey[];

export interface StageWords {
  /** "Étape 2 sur 5". */
  readonly count: string;
  /** Where the child is: "En route", or null when the track has no names. */
  readonly stage: string | null;
  /** What the screen shows: "Étape 2 sur 5 · En route", or "Au départ" at the start. */
  readonly where: string;
  /** What comes next, in a sentence. */
  readonly next: string;
  /** The screen reader's name for the climb. */
  readonly label: string;
}

function lowerFirst(text: string): string {
  return text.charAt(0).toLocaleLowerCase() + text.slice(1);
}

export function stageWords(t: Translate, skill: string, step: number, steps: number): StageWords {
  const named = steps === STAGES.length - 1;
  const nameOf = (n: number) => {
    const key = named ? STAGES[n] : undefined;
    return key === undefined ? t("progress.stepOf", { step: n, steps }) : t(key);
  };
  const count = t("progress.stepOf", { step, steps });
  const stage = named ? nameOf(step) : null;
  return {
    count,
    stage,
    where: stage === null ? count : step === 0 ? stage : `${count} · ${stage}`,
    next:
      step === 0
        ? t("progress.start")
        : step >= steps
          ? t("progress.summit")
          : t("progress.next", { stage: lowerFirst(nameOf(step + 1)) }),
    label:
      stage === null
        ? `${skill} · ${count}`
        : t("progress.steps", { skill, step, steps, stage: lowerFirst(stage) }),
  };
}

/** The moment's title for a stage reached: "Étape 3 : ça prend forme !". */
export function stageReached(t: Translate, step: number, steps: number): string {
  const key = steps === STAGES.length - 1 ? STAGES[step] : undefined;
  if (key === undefined) return t("progress.stepOf", { step, steps });
  return t("moment.step.title", { step, stage: lowerFirst(t(key)) });
}
